import { environment } from 'src/environments/environment';

import { inject, Injectable } from '@angular/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Platform } from '@ionic/angular/standalone';

import { ApiService } from './api.service';

/** Tope para la baja del token al cerrar sesión: sin red, el logout no espera. */
const UNREGISTER_TIMEOUT_MS = 3000;

/**
 * Token de FCM de esta instalación ante el API (`user/push-devices`). Vive aparte de
 * NotificationService para que AuthService pueda darlo de baja al cerrar sesión sin
 * dependencia circular (NotificationService inyecta AuthService).
 */
@Injectable({
  providedIn: 'root',
})
export class PushRegistrationService {
  private api = inject(ApiService);
  private platform = inject(Platform);

  /** Hay sesión con permiso de notificaciones: los tokens nuevos se registran. */
  #enabled = false;
  #registeredToken: string | null = null;

  /** Registra el token actual. Idempotente: no repite el alta del mismo token. */
  async enable(): Promise<void> {
    this.#enabled = true;

    try {
      const { token } = await FirebaseMessaging.getToken();
      await this.register(token);
    } catch {
      // iOS puede no tener aún el token de APNs: llega después por tokenReceived.
    }
  }

  /** FCM rotó el token (o lo entregó tarde): sólo cuenta con sesión activa. */
  async tokenChanged(token: string): Promise<void> {
    if (this.#enabled) {
      await this.register(token);
    }
  }

  /**
   * Cierre de sesión: baja del token en el API (mientras el token de sesión sigue
   * guardado) y borrado del token de FCM, que deja de valer aunque la baja falle.
   */
  async disable(): Promise<void> {
    const token = this.#registeredToken;
    this.#enabled = false;
    this.#registeredToken = null;

    if (!this.platform.is('hybrid')) return;

    const timeout = new Promise<void>((resolve) => setTimeout(resolve, UNREGISTER_TIMEOUT_MS));
    const unregister = (async () => {
      if (token) {
        await this.api.deleteData('user/push-devices', { token });
      }
      await FirebaseMessaging.deleteToken();
    })().catch(() => undefined);

    await Promise.race([unregister, timeout]);
  }

  private async register(token: string | undefined): Promise<void> {
    if (!token || token === this.#registeredToken) return;

    const res = await this.api.putData('user/push-devices', {
      token,
      platform: this.platform.is('ios') ? 'ios' : 'android',
      app_version: environment.version,
    });

    if (res?.success) {
      this.#registeredToken = token;
    }
  }
}
